// wysiwyg.common.components.singleaudioplayer.viewer.SingleAudioPlayer
define([
    'santa-components',
    'lodash',
    'reactDOM',
    'componentsCore',
    'audioCommon',
    'skins',
    'singleAudioPlayer/skins/skins.json'
], function (
    santaComponents,
    _,
    ReactDOM,
    componentsCore,
    audioCommon,
    skinsPackage,
    skinsJson
) {
    'use strict';

    const volumeToScale = function (volume) {
        return volume === 0 ? 0 : Math.ceil(volume / 20);
    };

    const scaleToVolume = function (volumeBars) {
        return volumeBars * 20;
    };

    const millisecondsToSeconds = function (milliseconds) {
        return milliseconds / 1000;
    };

    const secondsToMilliseconds = function (milliseconds) {
        return milliseconds * 1000;
    };

    const calculateDisplayDuration = function (time) {
        const minute = Math.floor(time / 60),
            second = Math.floor(time % 60),
            minuteDisplay = minute < 10 ? `0${minute}` : minute,
            secondDisplay = second < 10 ? `0${second}` : second,
            durationDisplay = `${minuteDisplay}:${secondDisplay}`;

        return durationDisplay;
    };

    const calculateSeekPosition = function (ev, width, duration) {
        const percents = (ev.nativeEvent.offsetX ? ev.nativeEvent.offsetX : ev.nativeEvent.layerX) / width;
        return Math.ceil(percents * duration);
    };

    const toggleMute = function (comp, compState) {
        if (compState === 'unmuted') {
            comp.muteAudio();
        } else {
            comp.unmuteAudio();
        }
    };

    /**
     * @class components.SingleAudioPlayer
     * @extends {core.skinBasedComp}
     * @extends {ReactCompositeComponent}
     * @extends {componentsCore.skinInfo}
     * @property {comp.properties} props
     * @extends {core.audioMixin}
     * @property {comp.properties} props
     */
    const singleAudioPlayer = {
        displayName: 'SingleAudioPlayer',
        mixins: [componentsCore.mixins.skinBasedComp, componentsCore.mixins.skinInfo, audioCommon.audioMixin],

        propTypes: {
            compData: santaComponents.santaTypesDefinitions.Component.compData.isRequired,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp.isRequired,
            styleId: santaComponents.santaTypesDefinitions.Component.styleId.isRequired
        },

        getInitialState() {
            // we need this as member values because of audioMixin,
            // (for example AudioPlayer has the "autoplay" value on compData object)
            this.audioVolume = this.props.compProp.volume;
            this.autoPlay = this.props.compProp.autoplay;

            return {
                $playerState: 'waiting',
                $device: this.getDeviceState(),
                $isduration: 'duration',
                $isMuted: 'unmuted',
                trackDuration: 0,
                progressPosition: 0,
                volumeBars: volumeToScale(this.props.compProp.volume),
                dragging: false,
                $heightChanged: false
            };
        },

        finishedPlayingAudio() {
            if (this.props.compProp.loop) {
                this.playAudio();
            } else {
                this.initiatePause();
                this.setState({$playerState: 'repeat'});
            }
        },

        whileLoadingHandler(trackDuration) {
            this.setState({
                trackDuration: millisecondsToSeconds(trackDuration)
            });
        },

        whilePlayingHandler(progressPosition) {
            this.setState({
                progressPosition: millisecondsToSeconds(progressPosition)
            });
        },

        getProgressBarWidth() {
            return ReactDOM.findDOMNode(this.refs.progressbar).offsetWidth;
        },

        resetTrackPosition() {
            this.setState({
                progressPosition: 0
            });
        },

        movingProgressbarHandle(ev) {
            const width = this.getProgressBarWidth(),
                barOffsetLeft = this.getSkinExports('barSpaceLeft').barSpaceLeft,
                barOffsetRight = this.getSkinExports('barSpaceRight').barSpaceRight,
                compWidth = this.props.style.width,
                mouseOffset = ev.pageX,
                duration = this.getAudioDuration(),
                outOfBounds = mouseOffset < barOffsetLeft || mouseOffset > compWidth - barOffsetRight,
                seekPosition = calculateSeekPosition(ev, width, duration);

            if (outOfBounds) {
                return;
            }
            this.setState({
                progressPosition: seekPosition / duration * 100
            });
            this.seekAudio(seekPosition);
        },

        stoppedMovingProgressbarHandle(ev) {
            const width = this.getProgressBarWidth();
            const duration = this.getAudioDuration();
            const seekPosition = calculateSeekPosition(ev, width, duration);
            this.setState({
                progressPosition: seekPosition / duration * 100
            });
            this.seekAudio(seekPosition);
        },

        callSeek(ev) {
            const width = this.getProgressBarWidth();
            const duration = this.getAudioDuration();
            const seekPosition = calculateSeekPosition(ev, width, duration);

            this.seekAudio(seekPosition);
        },

        // **** Volume functions **** //
        buildVolumeScale() {
            const scaleLength = 5,
                styleId = this.props.styleId,
                turnOnBars = this.state.volumeBars;

            return _.times(scaleLength, function (index) {
                return santaComponents.utils.createReactElement('li', {
                    className: index < turnOnBars ? `${styleId}_on` : `${styleId}_off`,
                    onClick: this.handleVolumeBarClick,
                    'data-index': index + 1
                }, santaComponents.utils.createReactElement('div', {className: `${styleId}_colorBlock`}), santaComponents.utils.createReactElement('div', {className: `${styleId}_colorBlank`}));
            }.bind(this));
        },

        callToggleMute() {
            toggleMute(this, this.state.$isMuted);
            if (this.state.$isMuted === 'unmuted') {
                this.setState({$isMuted: 'muted'});
            } else {
                this.setState({$isMuted: 'unmuted'});
            }
        },

        getTargetIndex(target) {
            return target.getAttribute('data-index');
        },

        setNonPersistentVolume(volumeBars) {
            volumeBars = Math.min(volumeBars, 5);
            volumeBars = Math.max(volumeBars, 0);

            this.setState({volumeBars});
            this.setVolume(scaleToVolume(volumeBars));
        },

        handleVolumeBarClick(event) {
            const targetIndex = this.getTargetIndex(event.currentTarget);
            this.setNonPersistentVolume(targetIndex);
        },

        updateComponentHeight(device) {
            return this.getSkinExports(`${device}Height`)[`${device}Height`];
        },

        handleMediaButtonPress() {
            switch (this.state.$playerState) {
                case 'waiting':
                case 'pausing':
                case 'repeat':
                    this.initiatePlay();
                    break;
                case 'playing':
                    this.initiatePause();
                    break;
            }
        },

        getMediaButtonAriaLabel() {
            switch (this.state.$playerState) {
                case 'waiting':
                case 'pausing':
                    return 'Play';
                case 'playing':
                    return 'Pause';
                case 'repeat':
                    return 'Repeat';
            }
        },

        changeTrackPlayingPosition(position) {
            const duration = this.state.trackDuration;

            position = Math.min(position, duration);
            position = Math.max(position, 0);

            this.setState({
                progressPosition: position
            });

            this.seekAudio(secondsToMilliseconds(position));
        },

        handleAudioPlayerKeyDown(event) {
            const TRACK_PROGRESS_STEP = this.state.trackDuration * 0.1;
            switch (event.key) {
                case 'ArrowLeft':
                    this.changeTrackPlayingPosition(this.state.progressPosition - TRACK_PROGRESS_STEP);
                    event.preventDefault();
                    break;
                case 'ArrowRight':
                    this.changeTrackPlayingPosition(this.state.progressPosition + TRACK_PROGRESS_STEP);
                    event.preventDefault();
                    break;
                case 'ArrowUp':
                    this.setNonPersistentVolume(this.state.volumeBars + 1);
                    event.preventDefault();
                    break;
                case 'ArrowDown':
                    this.setNonPersistentVolume(this.state.volumeBars - 1);
                    event.preventDefault();
                    break;
            }
        },

        isMuteToggled() {
            return this.state.$isMuted !== 'unmuted';
        },

        getVolumeLabel() {
            const volumePercentage = `${scaleToVolume(this.state.volumeBars)}% volume`;
            const suffixIfMuted = this.isMuteToggled() ? ', muted' : '';
            return volumePercentage + suffixIfMuted;
        },

        getProgressPercentage() {
            return Math.floor(this.state.progressPosition / this.state.trackDuration * 100);
        },

        getSkinProperties() {
            this.autoPlay = this.props.compProp.autoplay;
            this.updateAudioObject();
            return {
                '': {
                    style: {
                        height: this.updateComponentHeight(this.getDeviceState())
                    }
                },
                'player': {
                    role: 'application',
                    'aria-label': `Application, Audio player, ${this.props.compData.track}`,
                    onKeyDown: this.handleAudioPlayerKeyDown
                },
                'sep': {
                    children: ' - '
                },
                'sep2': {
                    children: ' / '
                },
                'artistLabel': {
                    children: this.props.compData.artist
                },
                'trackLabel': {
                    children: this.props.compData.track
                },
                'mediaButton': {
                    onClick: this.handleMediaButtonPress,
                    'aria-label': this.getMediaButtonAriaLabel()
                },
                'bar': {
                    onClick: this.callSeek
                },
                'slider': {
                    onClick: this.callSeek,
                    style: {
                        'width': `${this.getProgressPercentage()}%`
                    }
                },
                'handle': {
                    onDrag: this.movingProgressbarHandle,
                    onDragEnd: this.stoppedMovingProgressbarHandle,
                    style: {
                        cursor: 'pointer',
                        'left': `${this.getProgressPercentage()}%`
                    },
                    draggable: true
                },
                'volumeBtn': {
                    onClick: this.callToggleMute,
                    'aria-label': 'Mute',
                    'aria-pressed': this.isMuteToggled()
                },
                'volumeControls': {
                    onKeyPress: this.handleVolumeBtnKeyPress
                },
                'volumeScale': {
                    tabIndex: '0',
                    children: this.buildVolumeScale(),
                    role: 'slider',
                    'aria-valuemin': '0',
                    'aria-valuemax': '100',
                    'aria-valuenow': this.state.volumeBars * 20,
                    'aria-valuetext': this.getVolumeLabel()
                },
                'progressbar': {
                    tabIndex: '0',
                    'role': 'progressbar',
                    'aria-valuemin': '0',
                    'aria-valuemax': this.state.trackDuration,
                    'aria-valuenow': this.state.progressPosition,
                    'aria-valuetext': calculateDisplayDuration(this.state.progressPosition)
                },
                'trackDuration': {
                    children: calculateDisplayDuration(this.state.trackDuration)
                },
                'trackPosition': {
                    children: calculateDisplayDuration(this.state.progressPosition)
                }
            };
        }
    };

    componentsCore.compRegistrar.register('wysiwyg.common.components.singleaudioplayer.viewer.SingleAudioPlayer', singleAudioPlayer);
    skinsPackage.skinsMap.addBatch(skinsJson);

    return singleAudioPlayer;
});
